import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { useMe } from '../session.js';
import { CheckIcon } from './icons.js';
import { Card } from './ui.js';

type Step = 'site' | 'verify' | 'alertTest' | 'contentWatch';

// Stored per device; older versions stored "hidden" under the same key, which now means collapsed.
const collapseKey = (accountId: string) => `wg:firstSteps:hidden:${accountId}`;
const readCollapsed = (accountId: string) => {
  try {
    return window.localStorage.getItem(collapseKey(accountId)) === '1';
  } catch {
    return false;
  }
};
const storeCollapsed = (accountId: string, collapsed: boolean) => {
  try {
    if (collapsed) window.localStorage.setItem(collapseKey(accountId), '1');
    else window.localStorage.removeItem(collapseKey(accountId));
  } catch {
    // Private mode: the choice lasts until the page is reloaded.
  }
};

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    viewBox="0 0 20 20"
    width="20"
    height="20"
    aria-hidden="true"
    className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
  >
    <path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

/**
 * First steps of a new account (T-65): add a site, confirm ownership, send a test alert, watch a
 * phone number or text. Shown on the overview until done; it can be collapsed into one line with the
 * progress and opened again (remembered on this device). T-92: a horizontal path of steps, the
 * current one explained and highlighted.
 */
export function FirstSteps() {
  const { t } = useTranslation();
  const accountId = useMe().data?.account.id ?? '';
  // null: follow the stored choice (read on every render: the account id arrives after the first).
  const [choice, setChoice] = useState<boolean | null>(null);
  const collapsed = choice ?? (accountId !== '' && readCollapsed(accountId));
  const state = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => unwrap(api.GET('/onboarding')),
  });
  const data = state.data;
  if (!data || data.completed) return null;

  const site = data.siteId;
  const steps: { key: Step; done: boolean; to: string }[] = [
    { key: 'site', done: data.steps.site, to: '/sites/new' },
    {
      key: 'verify',
      done: data.steps.verify,
      to: site ? `/sites/${site}/settings` : '/sites/new',
    },
    { key: 'alertTest', done: data.steps.alertTest, to: '/notifications' },
    ...(data.contentWatchAvailable
      ? [
          {
            key: 'contentWatch' as const,
            done: data.steps.contentWatch,
            to: site ? `/sites/${site}/settings` : '/sites/new',
          },
        ]
      : []),
  ];
  const done = steps.filter((s) => s.done).length;
  const toggle = () => {
    storeCollapsed(accountId, !collapsed);
    setChoice(!collapsed);
  };
  const progress = (
    <div
      className="h-2 overflow-hidden rounded-full bg-slate-200"
      role="progressbar"
      aria-label={t('firstSteps.title')}
      aria-valuemin={0}
      aria-valuemax={steps.length}
      aria-valuenow={done}
    >
      <div
        className="h-full rounded-full bg-green-700"
        style={{ width: `${Math.round((done / steps.length) * 100)}%` }}
      />
    </div>
  );

  if (collapsed)
    return (
      <button
        type="button"
        onClick={toggle}
        aria-expanded={false}
        className="mb-4 flex min-h-11 w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-slate-200 bg-white px-4 py-2 text-left shadow-sm hover:border-blue-300 hover:bg-blue-50 focus-visible:outline-2 focus-visible:outline-blue-700"
      >
        <span className="font-semibold">{t('firstSteps.title')}</span>
        <span className="text-sm text-slate-700">
          {t('firstSteps.progress', { done, total: steps.length })}
        </span>
        <span className="min-w-24 flex-1">{progress}</span>
        <span className="flex items-center gap-1 text-sm font-semibold text-blue-800">
          {t('firstSteps.expand')}
          <Chevron open={false} />
        </span>
      </button>
    );

  // The first open step is the one to do now; it gets its explanation and a strong button.
  const current = steps.findIndex((s) => !s.done);
  return (
    <Card className="mb-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold">{t('firstSteps.title')}</h2>
          <p className="text-slate-700">
            {t('firstSteps.progress', { done, total: steps.length })}
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          aria-expanded={true}
          className="flex min-h-11 items-center gap-1 rounded-md px-2 text-sm font-semibold text-blue-800 hover:bg-blue-50"
        >
          {t('firstSteps.collapse')}
          <Chevron open={true} />
        </button>
      </div>
      <div className="mt-3">{progress}</div>
      {/* T-92: a horizontal path from step to step on wide screens, a list on phones. */}
      <ol className="mt-5 grid gap-4 md:grid-flow-col md:auto-cols-fr md:gap-0">
        {steps.map((s, i) => {
          const now = i === current;
          return (
            <li key={s.key} className="relative flex gap-3 md:flex-col md:gap-2 md:pr-4">
              {i < steps.length - 1 ? (
                <span
                  aria-hidden="true"
                  className={`absolute top-4 left-10 hidden h-0.5 w-[calc(100%-2.5rem)] md:block ${s.done ? 'bg-green-600' : 'bg-slate-200'}`}
                />
              ) : null}
              <span
                aria-hidden="true"
                className={`relative grid size-8 shrink-0 place-items-center rounded-full text-sm font-bold ${
                  s.done
                    ? 'bg-green-700 text-white'
                    : now
                      ? 'bg-blue-700 text-white ring-4 ring-blue-100'
                      : 'border-2 border-slate-300 bg-white text-slate-700'
                }`}
              >
                {s.done ? <CheckIcon /> : i + 1}
              </span>
              <div className="flex min-w-0 flex-col items-start gap-1">
                <strong className={s.done ? 'text-green-900' : now ? 'text-blue-950' : ''}>
                  {t(`firstSteps.steps.${s.key}.title`)}
                </strong>
                <span className="sr-only">
                  {' '}
                  – {s.done ? t('firstSteps.done') : t('firstSteps.open')}
                </span>
                {now ? (
                  <span className="text-sm text-slate-700">
                    {t(`firstSteps.steps.${s.key}.text`)}
                  </span>
                ) : null}
                {!s.done ? (
                  <Link
                    to={s.to}
                    className={`mt-1 inline-flex min-h-10 items-center rounded-md px-3 text-sm font-semibold ${
                      now
                        ? 'bg-blue-700 text-white hover:bg-blue-800'
                        : 'border border-slate-300 text-slate-900 hover:bg-blue-50'
                    }`}
                  >
                    {t(`firstSteps.steps.${s.key}.action`)}
                  </Link>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
